import { useState, useCallback } from 'react';
import { CreateUserRequest, UpdateUserRequest, UsersState, User } from '../types/users.types';
import { UsersService } from '../services/users.service';

const initialState: UsersState = {
  users: [],
  currentUser: null,
  isLoading: false,
  formLoading: false,
  error: null,
};

export const useUsers = () => {
  const [state, setState] = useState<UsersState>(initialState);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading, error: null }));
  }, []);

  const setFormLoading = useCallback((formLoading: boolean) => {
    setState(prev => ({ ...prev, formLoading, error: null }));
  }, []);

  const setError = useCallback((error: string) => {
    setState(prev => ({ ...prev, error, isLoading: false, formLoading: false }));
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const users = await UsersService.getUsers();
      setState(prev => ({
        ...prev,
        users,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch users';
      setError(errorMessage);
    }
  }, [setLoading, setError]);

  const fetchUserById = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const user = await UsersService.getUserById(id);
      setState(prev => ({
        ...prev,
        currentUser: user,
        isLoading: false,
        error: null,
      }));
      return user;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch user';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setError]);

  const createUser = useCallback(async (userData: CreateUserRequest) => {
    try {
      setFormLoading(true);
      const newUser = await UsersService.createUser(userData);
      setState(prev => ({
        ...prev,
        users: [newUser, ...prev.users],
        formLoading: false,
        error: null,
      }));
      return newUser;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create user';
      setError(errorMessage);
      throw error;
    }
  }, [setFormLoading, setError]);

  const updateUser = useCallback(async (id: string, userData: UpdateUserRequest) => {
    try {
      setFormLoading(true);
      const updatedUser = await UsersService.updateUser(id, userData);
      setState(prev => ({
        ...prev,
        users: prev.users.map(user => 
          user.id === id ? updatedUser : user
        ),
        currentUser: prev.currentUser?.id === id ? updatedUser : prev.currentUser,
        formLoading: false,
        error: null,
      }));
      return updatedUser;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update user';
      setError(errorMessage);
      throw error;
    }
  }, [setFormLoading, setError]);

  const deleteUser = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await UsersService.deleteUser(id);
      setState(prev => ({
        ...prev,
        users: prev.users.filter(user => user.id !== id),
        currentUser: prev.currentUser?.id === id ? null : prev.currentUser,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setError]);

  const setCurrentUser = useCallback((user: User) => {
    setState(prev => ({ ...prev, currentUser: user }));
  }, []);

  const clearCurrentUser = useCallback(() => {
    setState(prev => ({ ...prev, currentUser: null }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    fetchUsers,
    fetchUserById,
    createUser,
    updateUser,
    deleteUser,
    setCurrentUser,
    clearCurrentUser,
    clearError,
  };
}; 